import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PortalAccount } from '@/utils/auth';

/**
 * 门户账号 Context
 *
 * 目的：把 `/portal/me` 返回的用户信息（name / organization 等）**只**放在
 * 内存态中，避免使用 localStorage/sessionStorage 持久化——那些存储用户可通过
 * DevTools 直接改写，导致前端基于 organization 的分支（如"上海管局"多选）
 * 被绕过。
 *
 * - 页面刷新时 profile 会丢失 → 由 `LoginEntryGate` / `PortalGuard` 重新拉
 *   `/portal/me` 兜底恢复。
 * - 后端接口鉴权/授权**不**依赖前端上送的 organization/name，权限一律以后端
 *   反查的 `t_account` 为准；这里仅用于 UI 展示与前端交互开关，安全性由后端保证。
 */
interface PortalAccountContextValue {
  account: PortalAccount | null;
  setAccount: (account: PortalAccount | null) => void;
}

const PortalAccountContext = createContext<PortalAccountContextValue | null>(null);

interface Props {
  children: ReactNode;
}

export const PortalAccountProvider = ({ children }: Props) => {
  const [account, setAccountState] = useState<PortalAccount | null>(null);

  const setAccount = useCallback((next: PortalAccount | null) => {
    setAccountState(next);
  }, []);

  const value = useMemo(() => ({ account, setAccount }), [account, setAccount]);

  return (
    <PortalAccountContext.Provider value={value}>{children}</PortalAccountContext.Provider>
  );
};

/**
 * 消费门户账号 Context。
 *
 * 在 Provider 之外调用会抛错——这有助于在开发期尽早暴露"忘记挂 Provider"的问题；
 * 但为了兼容后台管理页面（不在 Provider 下）的极少数意外调用，这里改为返回
 * `{ account: null, setAccount: noop }` 的降级值，调用方拿到 null 时按未登录处理。
 */
export const usePortalAccount = (): PortalAccountContextValue => {
  const ctx = useContext(PortalAccountContext);
  if (ctx) return ctx;
  return {
    account: null,
    setAccount: () => {
      /* 在 Provider 之外调用，静默降级 */
    },
  };
};
